import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/features/auth/AuthProvider';
import { getEegFileMeta, listSessions, type SessionSummariesPage } from './api';
import {
  effectiveDurationSec,
  sessionTimestamp,
  type SessionSummary,
} from './model';

/** Query keys are namespaced by user id so a sign-out can never leak a cache. */
export const sessionKeys = {
  all: ['sessions'] as const,
  list: (userId: string) => ['sessions', 'list', userId] as const,
};

/**
 * Loads the signed-in user's sessions.
 *
 * Everything downstream — the overview, the table, the detail page — reads
 * from this one query, so a session list is fetched once per sign-in and a
 * detail page opened from the table renders instantly.
 */
export function useSessions() {
  const { userId, status } = useAuth();

  const query = useQuery<SessionSummariesPage>({
    queryKey: sessionKeys.list(userId ?? 'anonymous'),
    queryFn: () => listSessions(userId as string),
    enabled: status === 'authenticated' && userId !== null,
    staleTime: 60_000,
  });

  return {
    ...query,
    sessions: query.data?.sessions ?? [],
    skipped: query.data?.skipped ?? 0,
  };
}

/** Finds one session in the already-loaded list. */
export function useSession(sessionId: string | undefined) {
  const { sessions, isPending, isError, error, refetch } = useSessions();

  const session = useMemo(
    () => (sessionId ? (sessions.find((item) => item.id === sessionId) ?? null) : null),
    [sessions, sessionId],
  );

  return { session, isPending, isError, error, refetch, loaded: sessions.length > 0 };
}

/**
 * Checks that the session's timeline file (`timeline_file_id`) actually exists
 * in the EEG bucket — a metadata lookup only, the file itself is not fetched.
 *
 * `checking` while the lookup runs, `exists` when the file is there, and
 * `missing` when there is no id or the file is gone / not readable.
 */
export type TimelineFileStatus = 'checking' | 'exists' | 'missing';

export function useTimelineFileStatus(session: SessionSummary | null | undefined): TimelineFileStatus {
  const fileId = session?.timelineFileId?.trim() ?? '';

  const query = useQuery({
    queryKey: ['sessions', 'timeline-meta', fileId] as const,
    queryFn: () => getEegFileMeta(fileId),
    enabled: fileId !== '',
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (fileId === '') return 'missing';
  if (query.isPending) return 'checking';
  return query.isSuccess ? 'exists' : 'missing';
}

// ---------------------------------------------------------------------------
// Derived analytics. Kept here rather than in components so the same numbers
// feed the overview, the analytics module and any future report.
// ---------------------------------------------------------------------------

export interface SessionStats {
  total: number;
  totalDurationSec: number;
  meanDurationSec: number | null;
  withRawData: number;
  /** Mean of the per-session `values_avg`, over the sessions that report one. */
  meanScore: number | null;
  bestScore: number | null;
  firstSession: Date | null;
  lastSession: Date | null;
  modes: { mode: string; count: number }[];
}

export function computeSessionStats(sessions: readonly SessionSummary[]): SessionStats {
  let totalDurationSec = 0;
  let durationCount = 0;
  let scoreSum = 0;
  let scoreCount = 0;
  let bestScore: number | null = null;
  let withRawData = 0;
  let first: Date | null = null;
  let last: Date | null = null;
  const modeCounts = new Map<string, number>();

  for (const session of sessions) {
    const duration = effectiveDurationSec(session);
    if (duration !== null) {
      totalDurationSec += duration;
      durationCount += 1;
    }

    if (session.valuesAvg !== null) {
      scoreSum += session.valuesAvg;
      scoreCount += 1;
    }
    if (session.valuesMax !== null && (bestScore === null || session.valuesMax > bestScore)) {
      bestScore = session.valuesMax;
    }

    if (session.rawFileId !== null) withRawData += 1;

    const timestamp = sessionTimestamp(session);
    if (timestamp !== null) {
      if (first === null || timestamp < first) first = timestamp;
      if (last === null || timestamp > last) last = timestamp;
    }

    const mode = session.mode ?? 'unspecified';
    modeCounts.set(mode, (modeCounts.get(mode) ?? 0) + 1);
  }

  return {
    total: sessions.length,
    totalDurationSec,
    meanDurationSec: durationCount > 0 ? Math.round(totalDurationSec / durationCount) : null,
    withRawData,
    meanScore: scoreCount > 0 ? scoreSum / scoreCount : null,
    bestScore,
    firstSession: first,
    lastSession: last,
    modes: [...modeCounts.entries()]
      .map(([mode, count]) => ({ mode, count }))
      .sort((a, b) => b.count - a.count),
  };
}

export interface ActivityPoint {
  /** ISO date, midnight local. */
  date: string;
  label: string;
  sessions: number;
  minutes: number;
}

/**
 * Sessions and minutes per day over the last `days` days, including the days
 * with nothing in them — a gap in practice is information, and dropping empty
 * days would silently compress the time axis.
 */
export function buildActivitySeries(
  sessions: readonly SessionSummary[],
  days: number,
): ActivityPoint[] {
  const byDay = new Map<string, { sessions: number; seconds: number }>();

  for (const session of sessions) {
    const timestamp = sessionTimestamp(session);
    if (timestamp === null) continue;
    const key = dayKey(timestamp);
    const bucket = byDay.get(key) ?? { sessions: 0, seconds: 0 };
    bucket.sessions += 1;
    bucket.seconds += effectiveDurationSec(session) ?? 0;
    byDay.set(key, bucket);
  }

  const points: ActivityPoint[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    const key = dayKey(date);
    const bucket = byDay.get(key);
    points.push({
      date: key,
      label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sessions: bucket?.sessions ?? 0,
      minutes: Math.round((bucket?.seconds ?? 0) / 60),
    });
  }

  return points;
}

function dayKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}
